import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Container,
  Col,
  Row,
  Card,
  Form,
  FloatingLabel,
  OverlayTrigger,
  Tooltip,
  Spinner,
  Alert,
} from "react-bootstrap";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  BsBox,
  BsBuilding,
  BsCalendar,
  BsCalendarDate,
  BsCheck2,
  BsInfoCircle,
  BsPen,
  BsPerson,
  BsSignpost2,
  BsTruck,
} from "react-icons/bs";
import { RiShipLine } from "react-icons/ri";
import { FaTrailer } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import defaultSelectStyle from "../../utils/select";
import styles from "./bookingForm.module.scss";
import DatePicker from "react-datepicker";
import moment from "moment";
import bookingFormSlice from "./store/slice";
import BookingQuickView from "../booking-quick-view/BookingQuickView";
import countrySlice from "../countries/store/slice";
import routeSlice from "../routes/store/slice";
import { RouteRequest } from "../routes/store/saga";
import ConfirmModal from "../confirm-modal/ConfirmModal";
import { VehicleTypeCode } from "../vehicle-types/store/slice";
import { useNavigate, useParams } from "react-router-dom";
import userSlice, { RoleCode } from "../users/store/slice";
import InternalBadge from "../internal-badge/InternalBadge";
import documentSlice from "../documents/store/slice";
import { AvailableDaysRequest, ScheduleRequest } from "./store/saga";
import { hasRole } from "../../utils/roles";
import { uniqBy } from "lodash";
import { UserRequestParams } from "../users/store/saga";
import {
  formatDate,
  formatTimeFromTime,
  formatUTCDate,
  formatUTCTime,
} from "../../utils/date";
import { Driver } from "../booking-page/store/slice";
import LiveQueueBadge from "../live-queue-badge/LiveQueueBadge";

// const acceptedMimeTypes = {
//   "application/pdf": [],
//   "application/msword": [],
//   "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
//   "text/csv": [],
//   "text/plain": [],
//   "application/vnd.ms-excel": [],
//   "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
//   "application/vnd.oasis.opendocument.spreadsheet": [],
//   "application/vnd.oasis.opendocument.text": [],
//   "image/jpeg": [],
//   "image/png": [],
// };

export type DefaultOption = {
  value: number | string;
  label: string;
};

export type DriverFormValues = {
  id: number;
  first_name: string;
  last_name: string;
  phone?: string;
  birth_date?: string;
  document_number: string;
  gender?: number;
  document_expiration_date?: string;
  country?: DefaultOption | null;
  document_country?: DefaultOption | null;
};

export type BookingFormValues = {
  customer: DefaultOption | null;
  departure_country: DefaultOption | null;
  arrival_country: DefaultOption | null;
  route: DefaultOption | null;
  date: any;
  time?: string;
  vehicle_number?: string;
  trailer_number?: string;
  vehicle_registration_country?: DefaultOption | null;
  trailer_registration_country?: DefaultOption | null;
  vehicle_type: DefaultOption | null;
  trailer_type: DefaultOption | null;
  vehicle_length?: number;
  vehicle_width?: number;
  vehicle_weight?: number;
  plug_in?: boolean;
  driver_count: string;
  drivers: DriverFormValues[];
  cargo_type: DefaultOption | null;
  adr?: boolean;
  customer_reference: string;
  temperature?: string;
};

type Props = {
  handleClose: () => void;
};

const BookingForm = ({ handleClose }: Props) => {
  const { id } = useParams<string>();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [show, setShow] = useState(true);
  const [isConfirmationVisible, setIsConfirmationVisible] =
    useState<boolean>(false);
  const [isWarningVisible, setIsWarningVisible] = useState<boolean>(false);

  const users = useSelector((state) => state.users.data);
  const usersLoading = useSelector((state) => state.users.isLoading);
  const profile = useSelector((state) => state.loginPage.profile);
  const data = useSelector((state) => state.bookingForm.data);
  const isLoading = useSelector((state) => state.bookingForm.isLoading);
  const isFetching = useSelector((state) => state.bookingForm.isFetching);
  const modalMustBeClosed = useSelector(
    (state) => state.bookingForm.modalMustBeClosed
  );
  const routes = useSelector((state) => state.routes.data);
  const countries = useSelector((state) => state.countries.data);
  const departureCountries = useSelector(
    (state) => state.countries.departureData
  );
  const arrivalCountries = useSelector((state) => state.countries.arrivalData);
  const isLoadingCountries = useSelector((state) => state.countries.isLoading);
  const isLoadingDeparture = useSelector(
    (state) => state.countries.isLoadingDeparture
  );
  const isLoadingArrival = useSelector(
    (state) => state.countries.isLoadingArrival
  );
  const isRoutesLoading = useSelector((state) => state.routes.isLoading);
  const vehicleTypes = useSelector((state) => state.vehicleTypes.data);
  const trailerTypes = useSelector((state) => state.trailerTypes.data);
  const cargoTypes = useSelector((state) => state.cargoTypes.data);
  // const documents = useSelector((state) => state.documents.data);
  // const isDocumentsLoading = useSelector((state) => state.documents.isLoading);
  const schedules = useSelector((state) => state.bookingForm.schedule);
  const availableDays = useSelector((state) => state.bookingForm.availableDays);
  const isScheduleLoading = useSelector(
    (state) => state.bookingForm.isScheduleLoading
  );

  // const uploadDocuments = useCallback(
  //   (documents: File[]) => dispatch(documentSlice.actions.upload(documents)),
  //   [dispatch]
  // );
  // const deleteDocument = useCallback(
  //   (id: number) => dispatch(documentSlice.actions.delete(id)),
  //   [dispatch]
  // );
  // const markToDeleteDocument = useCallback(
  //   (id: number) => dispatch(documentSlice.actions.removeData(id)),
  //   [dispatch]
  // );
  const fetchUsers = useCallback(
    (search: string) =>
      dispatch(
        userSlice.actions.fetch({
          search,
          roleCode: RoleCode.Customer,
          onlyWithCompanies: true,
        } as UserRequestParams)
      ),
    [dispatch]
  );
  const resetUsers = useCallback(
    () => dispatch(userSlice.actions.reset()),
    [dispatch]
  );
  const fetchBooking = useCallback(
    (id: number) => dispatch(bookingFormSlice.actions.fetchData(id)),
    [dispatch]
  );
  const resetCountries = useCallback(
    () => dispatch(countrySlice.actions.reset()),
    [dispatch]
  );
  const fetchRoutes = useCallback(
    (params: RouteRequest) => dispatch(routeSlice.actions.fetchData(params)),
    [dispatch]
  );
  const resetRoutes = useCallback(
    () => dispatch(routeSlice.actions.reset()),
    [dispatch]
  );
  const fetchSchedule = useCallback(
    (params: ScheduleRequest) =>
      dispatch(bookingFormSlice.actions.fetchSchedule(params)),
    [dispatch]
  );
  const fetchAvailableDays = useCallback(
    (params: AvailableDaysRequest) =>
      dispatch(bookingFormSlice.actions.fetchAvailableDays(params)),
    [dispatch]
  );
  const create = useCallback(
    (data: BookingFormValues) =>
      dispatch(bookingFormSlice.actions.submit(data)),
    [dispatch]
  );
  const update = useCallback(
    (id: number, data: BookingFormValues) =>
      dispatch(bookingFormSlice.actions.update({ id, data })),
    [dispatch]
  );
  const reset = useCallback(
    () => dispatch(bookingFormSlice.actions.reset()),
    [dispatch]
  );
  const resetDocuments = useCallback(
    () => dispatch(documentSlice.actions.reset()),
    [dispatch]
  );

  const {
    watch,
    register,
    unregister,
    formState: { isDirty, errors },
    control,
    handleSubmit,
    getValues,
    setValue,
  } = useForm<BookingFormValues>({
    defaultValues: {
      customer: null,
      driver_count: "0",
      drivers: [],
    },
  });

  const {
    fields: driverFields,
    append: addDriver,
    remove: removeDriver,
    replace: replaceDrivers,
  } = useFieldArray({
    control,
    name: "drivers",
  });

  const [
    driver_count,
    arrival_country,
    departure_country,
    date,
    time,
    route,
    adr,
    vehicle_type,
    trailer_type,
    customer,
  ] = watch([
    "driver_count",
    "arrival_country",
    "departure_country",
    "date",
    "time",
    "route",
    "adr",
    "vehicle_type",
    "trailer_type",
    "cargo_type",
    "customer",
  ]);

  const fetchCountries = useCallback(
    () => dispatch(countrySlice.actions.fetchData()),
    [dispatch]
  );
  const fetchCountriesDeparture = useCallback(
    () => dispatch(countrySlice.actions.fetchDepartureData()),
    [dispatch]
  );
  const fetchCountriesArrival = useCallback(
    () =>
      departure_country &&
      dispatch(countrySlice.actions.fetchArrivalData(departure_country.value)),
    [dispatch, departure_country]
  );

  const filterDisabledDays = useCallback(
    (date: any) => {
      if (!availableDays) {
        return false;
      }

      const dayOfWeek = moment(date).day();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
      const calendarDayDate = moment(date).toDate();

      const isAvailableDayOfWeek =
        availableDays.available_days_of_week.includes(adjustedDayOfWeek);
      const disabledPeriodForDay =
        availableDays.disabled_periods[adjustedDayOfWeek] ?? null;

      const isInDisabledPeriod = disabledPeriodForDay
        ? moment(calendarDayDate).isBetween(
            moment(disabledPeriodForDay.disabled_from),
            moment(disabledPeriodForDay.disabled_to),
            "days",
            "[]"
          )
        : false;

      return isAvailableDayOfWeek ? !isInDisabledPeriod : false;
    },
    [availableDays]
  );

  const selectedRoute = useMemo(() => {
    const selectedRouteOption = route;

    if (selectedRouteOption) {
      return routes.find((route) => route.id === selectedRouteOption.value);
    }
  }, [route, routes]);

  const selectedVehicleType = useMemo(() => {
    const selectedVehicleTypeOption = vehicle_type;

    if (selectedVehicleTypeOption) {
      return vehicleTypes.find(
        (vehicleType) => vehicleType.id === selectedVehicleTypeOption.value
      );
    }
  }, [vehicle_type, vehicleTypes]);

  const selectedTrailerType = useMemo(() => {
    const selectedTrailerTypeOption = trailer_type;

    if (selectedTrailerTypeOption) {
      return trailerTypes.find(
        (trailerType) => trailerType.id === selectedTrailerTypeOption.value
      );
    }
  }, [trailer_type, trailerTypes]);

  const customerOptions: any = useMemo(
    () =>
      uniqBy(
        users.flatMap((user) => user.companies!),
        "ax_code"
      ).map((uniqueCompany) => ({
        label: `${uniqueCompany.name} (${uniqueCompany.ax_code})`,
        options: users
          .filter((user) =>
            user.companies!.find(
              (company) => company.ax_code === uniqueCompany.ax_code
            )
          )
          .map((user) => ({
            label: user.name,
            value: user.id,
          })),
      })),
    [users]
  );

  const customerCompanyName = useMemo(() => {
    if (customer) {
      const user = users.find((user) => user.id === customer.value);

      if (user) {
        return user.companies?.[0]?.name;
      }
    }

    return null;
  }, [customer, users]);

  const cargoTypeOptions = useMemo(
    () =>
      uniqBy(
        cargoTypes.flatMap((cargoType) => cargoType.group!),
        "code"
      ).map((uniqueGroup) => ({
        label: uniqueGroup.name, // Group Name
        options: cargoTypes
          .filter((cargoType) => cargoType.group.code === uniqueGroup.code)
          .map((cargoType) => ({
            label: cargoType.name, // Option Name
            value: cargoType.id,
            groupLabel: uniqueGroup.name, // Store group name for filtering
          })),
      })),
    [cargoTypes]
  );

  // Custom filter function to search within groups as well
  const cargoTypeFilterOption = (option: any, input: string) => {
    if (!input) return true;
    const searchTerm = input.toLowerCase();
    return (
      option.label.toLowerCase().includes(searchTerm) || // Match option label
      option.data.groupLabel?.toLowerCase().includes(searchTerm) // Match group label
    );
  };

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        label: country.name,
        value: country.id,
      })),
    [countries]
  );

  const departureCountryOptions = useMemo(
    () =>
      !id
        ? departureCountries.map((country) => ({
            label: country.name,
            value: country.id,
          }))
        : [],
    [id, departureCountries]
  );

  const arrivalCountryOptions = useMemo(
    () =>
      !id
        ? arrivalCountries.map((country) => ({
            label: country.name,
            value: country.id,
          }))
        : [],
    [id, arrivalCountries]
  );

  const routeOptions = useMemo(
    () =>
      !id
        ? routes.map((route) => ({
            label: route.description,
            value: route.id,
          }))
        : [],
    [id, routes]
  );

  const vehicleTypeOptions = useMemo(() => {
    if (selectedRoute && !selectedRoute.all_vehicle_types) {
      return vehicleTypes
        .filter(
          (type) =>
            type.code !== VehicleTypeCode.Trailer &&
            type.code !== VehicleTypeCode.TrailerEmpty
        )
        .map((type) => ({
          label: type.name,
          value: type.id,
        }));
    }

    return vehicleTypes.map((type) => ({
      label: type.name,
      value: type.id,
    }));
  }, [vehicleTypes, selectedRoute]);

  const trailerTypeOptions = useMemo(
    () =>
      trailerTypes.map((type) => ({
        label: type.name,
        value: type.id,
      })),
    [trailerTypes]
  );

  const handleGentleClose = useCallback(() => {
    setTimeout(() => {
      handleClose();
    }, 100);
  }, [handleClose]);

  const handleBook = (data: BookingFormValues) => {
    setShow(false);

    setIsConfirmationVisible(true);
  };

  // const handleFileUpload = (uploadedDocuments: File[]) => {
  //   const existingFileNames = documents.map((document) => document.name);
  //   const uploadedFileNames = uploadedDocuments.map((file) => file.name);

  //   if (
  //     existingFileNames.some((existingFileName) =>
  //       uploadedFileNames.includes(existingFileName)
  //     )
  //   ) {
  //     alert("File with the same name already exists.");
  //     return;
  //   }

  //   if (documents.length + uploadedDocuments.length > 5) {
  //     alert("Maximum 5 files allowed.");
  //     return;
  //   }

  //   uploadDocuments(uploadedDocuments);
  // };

  // const handleRejectedFiles = (rejections: FileRejection[]) => {
  //   const rejection = rejections[0];
  //   const error = rejection.errors[0];

  //   alert(
  //     error.code === "file-too-large"
  //       ? `${rejection.file.name} is larger than 10MB.`
  //       : error.message
  //   );
  // };

  const handleUserInputChange = (inputValue: string, actionMeta: any) => {
    if (actionMeta.action === "input-change") {
      fetchUsers(inputValue);
    }
  };

  const hasTTLGroupForTrailerType = useMemo(
    () => selectedTrailerType?.group.code === "TLT",
    [selectedTrailerType]
  );

  const isVehicleTypeWithoutSeats = useMemo(
    () =>
      selectedVehicleType
        ? [VehicleTypeCode.Trailer, VehicleTypeCode.TrailerEmpty].includes(
            selectedVehicleType.code
          )
        : false,
    [selectedVehicleType]
  );

  useEffect(() => {
    const count = Number(driver_count);

    if (driverFields.length < count) {
      for (let i = driverFields.length; i < count; i++) {
        addDriver({
          first_name: undefined,
          last_name: undefined,
          phone: undefined,
          birth_date: undefined,
          document_number: undefined,
          gender: undefined,
          document_expiration_date: undefined,
          country: undefined,
          document_country: undefined,
        });
      }
    } else if (driverFields.length > count) {
      for (let i = driverFields.length; i > count; i--) {
        removeDriver(i - 1);
      }
    }
  }, [driver_count, driverFields, addDriver, removeDriver]);

  useEffect(() => {
    if (modalMustBeClosed) {
      handleGentleClose();
    }
  }, [modalMustBeClosed, handleGentleClose]);

  useEffect(() => {
    return () => {
      resetUsers();
    };
  }, [resetUsers]);

  useEffect(() => {
    if (id) {
      fetchBooking(Number(id));
    }

    return () => {
      reset();
      resetDocuments();
    };
  }, [id, fetchBooking, reset, resetDocuments]);

  useEffect(() => {
    fetchCountries();

    return () => {
      resetCountries();
    };
  }, [id, fetchCountries, resetCountries]);

  useEffect(() => {
    if (!id) {
      fetchCountriesDeparture();
    }

    return () => {
      resetCountries();
    };
  }, [id, fetchCountriesDeparture, resetCountries]);

  useEffect(() => {
    if (!id) {
      departure_country && setValue("arrival_country", null);
      departure_country && fetchCountriesArrival();
    }
  }, [id, departure_country, fetchCountriesArrival, setValue]);

  useEffect(() => {
    if (!id) {
      if (departure_country && arrival_country) {
        const params: RouteRequest = {
          departure_country_id: departure_country.value.toString(),
          arrival_country_id: arrival_country.value.toString(),
        };

        fetchRoutes(params);
      }

      return () => {
        resetRoutes();
      };
    }
  }, [id, departure_country, arrival_country, fetchRoutes, resetRoutes]);

  useEffect(() => {
    if (route && date) {
      const params: ScheduleRequest = {
        routeId: Number(route.value),
        date: formatDate(date),
      };

      fetchSchedule(params);
    }
  }, [id, route, date, fetchSchedule]);

  useEffect(() => {
    if (route) {
      const params: AvailableDaysRequest = {
        routeId: Number(route.value),
      };

      fetchAvailableDays(params);
    }
  }, [id, route, fetchSchedule, fetchAvailableDays]);

  // when booking is created
  useEffect(() => {
    if (!id && selectedVehicleType) {
      if (selectedVehicleType) {
        if (
          [VehicleTypeCode.Arctic, VehicleTypeCode.ArcticEmpty].includes(
            selectedVehicleType.code
          )
        ) {
          setValue("vehicle_length", 17);
          setValue("vehicle_width", 2.6);
        } else if (
          [VehicleTypeCode.Trailer, VehicleTypeCode.TrailerEmpty].includes(
            selectedVehicleType.code
          )
        ) {
          setValue("vehicle_length", 14);
          setValue("vehicle_width", 2.6);
        } else if (selectedVehicleType.code === VehicleTypeCode.OnlyTruck) {
          setValue("vehicle_width", 2.6);
        } else if (selectedVehicleType.code === VehicleTypeCode.Other) {
          setValue("vehicle_length", undefined);
          setValue("vehicle_width", undefined);
          setValue("vehicle_weight", undefined);
        }
      }
    }
  }, [id, selectedVehicleType, setValue]);

  // when booking is edited
  useEffect(() => {
    if (id && data && selectedVehicleType) {
      if (
        selectedVehicleType &&
        selectedVehicleType.id !== data.vehicle_type.id
      ) {
        if (
          [VehicleTypeCode.Arctic, VehicleTypeCode.ArcticEmpty].includes(
            selectedVehicleType.code
          )
        ) {
          setValue("vehicle_length", 17);
          setValue("vehicle_width", 2.6);
        } else if (
          [VehicleTypeCode.Trailer, VehicleTypeCode.TrailerEmpty].includes(
            selectedVehicleType.code
          )
        ) {
          setValue("vehicle_length", 14);
          setValue("vehicle_width", 2.6);
        } else if (selectedVehicleType.code === VehicleTypeCode.OnlyTruck) {
          setValue("vehicle_width", 2.6);
        } else if (selectedVehicleType.code === VehicleTypeCode.Other) {
          setValue("vehicle_length", undefined);
          setValue("vehicle_width", undefined);
          setValue("vehicle_weight", undefined);
        }
      }
    }
  }, [id, selectedVehicleType, data, setValue]);

  // when booking is edited
  useEffect(() => {
    if (id && data) {
      const { route, drivers } = data;

      setValue("customer", {
        value: data.owner.id,
        label: data.owner.name,
      });
      setValue("departure_country", {
        value: route.departure_country.id,
        label: route.departure_country.name,
      });
      setValue("arrival_country", {
        value: route.arrival_country.id,
        label: route.arrival_country.name,
      });
      setValue("route", {
        value: route.id,
        label: route.description,
      });
      setValue("date", new Date(formatUTCDate(data.departure_at)));

      setValue("time", route.id.toString());
      setValue("vehicle_number", data.vehicle_number);
      setValue("trailer_number", data.trailer_number);

      setValue(
        "vehicle_registration_country",
        data.vehicle_registration_country
          ? {
              value: data.vehicle_registration_country.id,
              label: data.vehicle_registration_country.name,
            }
          : null
      );

      setValue(
        "trailer_registration_country",
        data.trailer_registration_country
          ? {
              value: data.trailer_registration_country.id,
              label: data.trailer_registration_country.name,
            }
          : null
      );

      setValue("vehicle_type", {
        value: data.vehicle_type.id,
        label: data.vehicle_type.name,
      });

      if (data.trailer_type) {
        setValue("trailer_type", {
          value: data.trailer_type.id,
          label: data.trailer_type.name,
        });
      }

      setValue("vehicle_length", data.vehicle_length);
      setValue("vehicle_width", data.vehicle_width);
      setValue("vehicle_weight", data.vehicle_weight);

      if (data.cargo_type) {
        setValue("cargo_type", {
          value: data.cargo_type.id,
          label: data.cargo_type.name,
        });
      }

      setValue("temperature", data.temperature);

      if (data.route.adr && data.adr !== null) {
        setValue("adr", !!data.adr);
      }

      if (data.route.plug_in && data.plug_in !== null) {
        setValue("plug_in", !!data.plug_in);
      }

      setValue("driver_count", data.drivers.length.toString());
      setValue("customer_reference", data.customer_reference);

      if (drivers && drivers.length > 0) {
        replaceDrivers(
          drivers.map((driver: Driver) => ({
            id: driver.id,
            first_name: driver.first_name,
            last_name: driver.last_name,
            phone: driver.phone,
            birth_date: driver.birth_date,
            document_number: driver.document_number,
            gender: driver.gender.toString() as any,
            document_expiration_date: driver.document_expiration_date,
            country: driver.country
              ? {
                  value: driver.country.id,
                  label: driver.country.name,
                }
              : null,
            document_country: driver.document_country
              ? {
                  value: driver.document_country.id,
                  label: driver.document_country.name,
                }
              : null,
          }))
        );
      }
    }
  }, [id, data, replaceDrivers, setValue]);

  // when booking is edited
  useEffect(() => {
    if (id && data && date && schedules.length > 0) {
      // Selected day is the same as saved day
      if (formatDate(date) === formatUTCDate(data.departure_at)) {
        const schedule = schedules.find(
          (schedule) =>
            formatTimeFromTime(schedule.departure_time) ===
            formatUTCTime(data.departure_at)
        );

        if (schedule) {
          setValue("time", schedule.id.toString());
        }
      }
    }
  }, [id, data, date, schedules, setValue]);

  const isOtherVehicleType = useMemo(() => {
    if (selectedVehicleType) {
      return selectedVehicleType.code === VehicleTypeCode.Other;
    }

    return false;
  }, [selectedVehicleType]);

  const ferryCompanyName = useMemo(() => {
    if (data) {
      return data.route.company_name;
    }

    return selectedRoute ? selectedRoute.company_name : null;
  }, [data, selectedRoute]);

  const departureAt = useMemo(() => {
    if (date && time) {
      const selectedSchedule = schedules.find(
        (schedule) => schedule.id === Number(time)
      );

      if (selectedSchedule) {
        return `${formatDate(date)} ${selectedSchedule.departure_time}`;
      }
    }

    return null;
  }, [date, time, schedules]);

  const arrivalAt = useMemo(() => {
    if (date && time) {
      const selectedSchedule = schedules.find(
        (schedule) => schedule.id === Number(time)
      );

      if (selectedSchedule) {
        return `${moment(date)
          .add(
            selectedSchedule.arrival_day - selectedSchedule.departure_day,
            "days"
          )
          .format("YYYY-MM-DD")} ${selectedSchedule.arrival_time}`;
      }
    }

    return null;
  }, [date, time, schedules]);

  const duration = useMemo(() => {
    if (date && time) {
      const selectedSchedule = schedules.find(
        (schedule) => schedule.id === Number(time)
      );

      if (selectedSchedule) {
        return selectedSchedule.duration;
      }
    }

    return null;
  }, [date, time, schedules]);

  const selectedSchedule = useMemo(
    () => schedules.find((schedule) => schedule.id === Number(time)),
    [schedules, time]
  );

  const isMandatoryRegCountry = useMemo(
    () => !!selectedSchedule?.mandatory_reg_country,
    [selectedSchedule]
  );

  const shouldShowVehicleRegCountry = useMemo(() => {
    if (!isMandatoryRegCountry || !selectedVehicleType) return false;

    return [
      VehicleTypeCode.Arctic,
      VehicleTypeCode.ArcticEmpty,
      VehicleTypeCode.OnlyTruck,
      VehicleTypeCode.Other,
    ].includes(selectedVehicleType.code);
  }, [isMandatoryRegCountry, selectedVehicleType]);

  const shouldShowTrailerRegCountry = useMemo(() => {
    if (!isMandatoryRegCountry || !selectedVehicleType) return false;

    return [
      VehicleTypeCode.Arctic,
      VehicleTypeCode.ArcticEmpty,
      VehicleTypeCode.Trailer,
      VehicleTypeCode.TrailerEmpty,
      VehicleTypeCode.Other,
    ].includes(selectedVehicleType.code);
  }, [isMandatoryRegCountry, selectedVehicleType]);

  const isVehicleRegCountryRequired = useMemo(() => {
    if (!isMandatoryRegCountry || !selectedVehicleType) return false;

    return [
      VehicleTypeCode.Arctic,
      VehicleTypeCode.ArcticEmpty,
      VehicleTypeCode.OnlyTruck,
    ].includes(selectedVehicleType.code);
  }, [isMandatoryRegCountry, selectedVehicleType]);

  const isTrailerRegCountryRequired = useMemo(() => {
    if (!isMandatoryRegCountry || !selectedVehicleType) return false;

    return [
      VehicleTypeCode.Arctic,
      VehicleTypeCode.ArcticEmpty,
      VehicleTypeCode.Trailer,
      VehicleTypeCode.TrailerEmpty,
    ].includes(selectedVehicleType.code);
  }, [isMandatoryRegCountry, selectedVehicleType]);

  useEffect(() => {
    if (!shouldShowVehicleRegCountry) {
      unregister("vehicle_registration_country");
    }
  }, [shouldShowVehicleRegCountry, unregister]);

  useEffect(() => {
    if (!shouldShowTrailerRegCountry) {
      unregister("trailer_registration_country");
    }
  }, [shouldShowTrailerRegCountry, unregister]);

  useEffect(() => {
    if (isVehicleTypeWithoutSeats) {
      setValue("driver_count", "0");
    }
  }, [isVehicleTypeWithoutSeats, setValue]);

  const showVehicleNumber = useMemo(
    () =>
      selectedVehicleType &&
      ![VehicleTypeCode.Trailer, VehicleTypeCode.TrailerEmpty].includes(
        selectedVehicleType.code
      ),
    [selectedVehicleType]
  );

  const isVehicleTypeOther = useMemo(
    () => selectedVehicleType?.code === VehicleTypeCode.Other,
    [selectedVehicleType]
  );

  const isVehicleTypeOnlyTruck = useMemo(
    () => selectedVehicleType?.code === VehicleTypeCode.OnlyTruck,
    [selectedVehicleType]
  );

  const isWeightRequired = useMemo(
    () =>
      selectedVehicleType &&
      selectedRoute?.tonnage_cargo &&
      [VehicleTypeCode.Arctic, VehicleTypeCode.Trailer].includes(
        selectedVehicleType.code
      ),
    [selectedVehicleType, selectedRoute]
  );

  const showCargoData = useMemo(
    () =>
      selectedVehicleType &&
      ![
        VehicleTypeCode.ArcticEmpty,
        VehicleTypeCode.TrailerEmpty,
        VehicleTypeCode.OnlyTruck,
      ].includes(selectedVehicleType.code),
    [selectedVehicleType]
  );

  useEffect(() => {
    if (!showCargoData) {
      unregister("vehicle_weight");
      unregister("cargo_type");
      unregister("temperature");
      unregister("adr");
      unregister("plug_in");
    }
  }, [showCargoData, unregister]);

  useEffect(() => {
    if (isVehicleTypeOnlyTruck) {
      unregister("trailer_type");
      unregister("trailer_number");
    }
  }, [isVehicleTypeOnlyTruck, unregister]);

  useEffect(() => {
    if (!showVehicleNumber) {
      unregister("vehicle_number");
    }
  }, [showVehicleNumber, unregister]);

  useEffect(() => {
    if (!(selectedTrailerType && !hasTTLGroupForTrailerType)) {
      unregister("temperature");
    }
  }, [selectedTrailerType, hasTTLGroupForTrailerType, unregister]);

  if (!profile) return null;

  const isScheduleSelected = !!(selectedRoute && date && time);

  return (
    <>
      <Modal
        show={show}
        onHide={() => {
          if (isDirty) {
            setShow(false);
            setIsWarningVisible(true);
          } else {
            handleGentleClose();
          }
        }}
        backdrop="static"
        size="xl"
        fullscreen="lg-down"
      >
        <Modal.Header closeVariant="white" closeButton>
          <Modal.Title className={styles.title}>
            <div className={styles.information}>
              <span>{id ? "Edit" : "New"} booking</span>
              {id && <small className="text-secondary">Booking ID: {id}</small>}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="kpi-form"
            className={styles.form}
            onSubmit={handleSubmit(handleBook)}
          >
            <Container fluid>
              <Row>
                <Col xl={8}>
                  {hasRole(profile, RoleCode.Internal) && (
                    <Row className={styles.marginBottom}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title className={styles.customerTitle}>
                              <div>
                                <BsPerson /> Customer
                              </div>{" "}
                              <div>
                                <InternalBadge />
                              </div>
                            </Card.Title>

                            <hr />

                            <Row>
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="customer"
                                  rules={{ required: true }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      onInputChange={handleUserInputChange}
                                      options={customerOptions}
                                      isLoading={usersLoading}
                                      placeholder="Search for customer"
                                      styles={defaultSelectStyle}
                                      isDisabled={!!id}
                                      isClearable
                                    />
                                  )}
                                />
                              </Col>
                              <Col className={styles.customerName}>
                                {customerCompanyName && (
                                  <div>
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={(props) => (
                                        <Tooltip
                                          id="customer-tooltip"
                                          {...props}
                                        >
                                          Customer company
                                        </Tooltip>
                                      )}
                                    >
                                      <span>
                                        <BsBuilding /> {customerCompanyName}
                                      </span>
                                    </OverlayTrigger>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col>
                      <Card>
                        <Card.Body>
                          <Card.Title>
                            <BsSignpost2 /> Route
                          </Card.Title>

                          <hr />

                          <Row className="gy-3">
                            <Col xs={12} sm={6}>
                              <Controller
                                control={control}
                                name="departure_country"
                                rules={{ required: !id }}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    isSearchable
                                    isClearable
                                    isLoading={isLoadingDeparture}
                                    options={departureCountryOptions}
                                    placeholder="Departure country"
                                    className={styles.select}
                                    styles={defaultSelectStyle}
                                    isDisabled={
                                      !!id || isLoadingDeparture || !!route
                                    }
                                  />
                                )}
                              />
                            </Col>

                            <Col>
                              <Controller
                                control={control}
                                name="arrival_country"
                                rules={{ required: !id }}
                                render={({ field }) => (
                                  <Select
                                    {...field}
                                    isSearchable
                                    isClearable
                                    isLoading={isLoadingArrival}
                                    options={arrivalCountryOptions}
                                    placeholder="Arrival country"
                                    className={styles.select}
                                    styles={defaultSelectStyle}
                                    isDisabled={
                                      !!id ||
                                      isLoadingArrival ||
                                      !!route ||
                                      !departure_country
                                    }
                                  />
                                )}
                              />
                            </Col>
                          </Row>

                          {!!(arrival_country && departure_country) && (
                            <Row className="gy-3" style={{ marginTop: "0rem" }}>
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="route"
                                  rules={{ required: !id }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      isSearchable
                                      isClearable
                                      isLoading={isRoutesLoading}
                                      options={routeOptions}
                                      placeholder="Select route"
                                      className={styles.select}
                                      styles={defaultSelectStyle}
                                      isDisabled={
                                        !!id || isRoutesLoading || !!date
                                      }
                                    />
                                  )}
                                />
                              </Col>
                              <Col className={styles.companyName}>
                                {ferryCompanyName && (
                                  <div>
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={(props) => (
                                        <Tooltip
                                          id="ferry-company-tooltip"
                                          {...props}
                                        >
                                          {selectedRoute && (
                                            <>
                                              {selectedRoute.ferry_line_id ===
                                                1 && (
                                                <>
                                                  The <strong>truck</strong>{" "}
                                                  number will serve as your
                                                  identifier at the ferry line.
                                                </>
                                              )}
                                              {selectedRoute.ferry_line_id ===
                                                2 && (
                                                <>
                                                  The <strong>booking</strong>{" "}
                                                  number will serve as your
                                                  identifier at the ferry line.
                                                </>
                                              )}
                                            </>
                                          )}
                                        </Tooltip>
                                      )}
                                    >
                                      <span>
                                        <RiShipLine /> {ferryCompanyName}{" "}
                                        <BsInfoCircle
                                          style={{ marginLeft: "0.3rem" }}
                                        />
                                      </span>
                                    </OverlayTrigger>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>

                  {selectedRoute && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title>
                              <BsCalendarDate /> Date
                            </Card.Title>

                            <hr />

                            <Row className="gy-3">
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="date"
                                  rules={{
                                    required: true,
                                    onChange: () => {
                                      setValue("time", undefined);
                                    },
                                  }}
                                  render={({ field }) => (
                                    <Form.Control
                                      {...field}
                                      as={DatePicker}
                                      selected={field.value}
                                      filterDate={filterDisabledDays}
                                      name="date"
                                      placeholderText="Select date"
                                      dateFormat="yyyy-MM-dd"
                                      isInvalid={!!errors.date}
                                      minDate={moment().toDate()}
                                      maxDate={moment()
                                        .add(28, "days")
                                        .toDate()}
                                      autoComplete="off"
                                      isClearable={schedules.length > 0}
                                      showMonthDropdown
                                      showYearDropdown
                                    />
                                  )}
                                />
                              </Col>
                              {selectedRoute && (
                                <Col className={styles.timeColumn}>
                                  {!!(
                                    isScheduleLoading && schedules.length === 0
                                  ) && (
                                    <Spinner
                                      size="sm"
                                      animation="border"
                                      className={styles.smallSpinner}
                                    />
                                  )}
                                  {!!(
                                    !id &&
                                    date &&
                                    !isScheduleLoading &&
                                    schedules.length === 0
                                  ) && (
                                    <span className="text-muted">
                                      No ferries found.
                                    </span>
                                  )}
                                  {!!(
                                    id &&
                                    data &&
                                    date &&
                                    !isScheduleLoading &&
                                    schedules.length === 0
                                  ) && (
                                    <>
                                      <Form.Check
                                        inline
                                        label={formatUTCTime(data.departure_at)}
                                        type="radio"
                                        checked
                                        disabled
                                      />
                                      {selectedRoute.is_transit && (
                                        <LiveQueueBadge
                                          validityDays={
                                            selectedRoute.validity_days
                                          }
                                        />
                                      )}
                                    </>
                                  )}
                                  {date && (
                                    <>
                                      {schedules.map((schedule, index) => (
                                        <Form.Check
                                          key={index}
                                          {...register("time", {
                                            required: true,
                                          })}
                                          inline
                                          label={formatTimeFromTime(
                                            schedule.departure_time
                                          )}
                                          value={schedule.id}
                                          type="radio"
                                          id={`time-${schedule.id}`}
                                        />
                                      ))}
                                      {selectedRoute.is_transit && (
                                        <LiveQueueBadge
                                          validityDays={
                                            selectedRoute.validity_days
                                          }
                                        />
                                      )}
                                    </>
                                  )}
                                </Col>
                              )}
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {isScheduleSelected && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title>
                              <BsTruck /> Vehicle
                            </Card.Title>

                            <hr />

                            <Row className="gy-3">
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="vehicle_type"
                                  rules={{ required: true }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      isSearchable
                                      isClearable
                                      isLoading={false}
                                      options={vehicleTypeOptions}
                                      placeholder="Vehicle type"
                                      className={styles.select}
                                      styles={defaultSelectStyle}
                                    />
                                  )}
                                />
                                <Form.Text>
                                  If your cargo has non-standard dimensions,
                                  please contact the manager.
                                </Form.Text>
                              </Col>

                              <Col />
                            </Row>

                            {(shouldShowVehicleRegCountry ||
                              shouldShowTrailerRegCountry) && (
                              <Row className="gy-3">
                                {shouldShowVehicleRegCountry ? (
                                  <Col xs={12} sm={6}>
                                    <Controller
                                      control={control}
                                      name="vehicle_registration_country"
                                      rules={{
                                        required: isVehicleRegCountryRequired
                                          ? "Vehicle registration country is required."
                                          : false,
                                      }}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          isSearchable
                                          isLoading={false}
                                          options={countryOptions}
                                          placeholder="Vehicle registration country"
                                          className={styles.select}
                                          styles={defaultSelectStyle}
                                          isClearable={false}
                                        />
                                      )}
                                    />
                                    {errors.vehicle_registration_country && (
                                      <Form.Text className="text-danger">
                                        {
                                          errors.vehicle_registration_country
                                            ?.message
                                        }
                                      </Form.Text>
                                    )}
                                  </Col>
                                ) : (
                                  <Col xs={12} sm={6} />
                                )}

                                {shouldShowTrailerRegCountry ? (
                                  <Col xs={12} sm={6}>
                                    <Controller
                                      control={control}
                                      name="trailer_registration_country"
                                      rules={{
                                        required: isTrailerRegCountryRequired
                                          ? "Trailer registration country is required."
                                          : false,
                                      }}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          isSearchable
                                          isLoading={false}
                                          options={countryOptions}
                                          placeholder="Trailer registration country"
                                          className={styles.select}
                                          styles={defaultSelectStyle}
                                          isClearable={false}
                                        />
                                      )}
                                    />
                                    {errors.trailer_registration_country && (
                                      <Form.Text className="text-danger">
                                        {
                                          errors.trailer_registration_country
                                            ?.message
                                        }
                                      </Form.Text>
                                    )}
                                  </Col>
                                ) : (
                                  <Col xs={12} sm={6} />
                                )}
                              </Row>
                            )}

                            {vehicle_type && (
                              <>
                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "1rem" }}
                                >
                                  {showVehicleNumber && (
                                    <Col xs={12} sm={6}>
                                      <FloatingLabel
                                        controlId="vehicle_number"
                                        label="Vehicle number"
                                        className={styles.input}
                                      >
                                        <Form.Control
                                          type="text"
                                          placeholder="Vehicle number"
                                          {...register("vehicle_number", {
                                            required: !isVehicleTypeOther,
                                          })}
                                          isInvalid={!!errors.vehicle_number}
                                        />
                                      </FloatingLabel>
                                    </Col>
                                  )}
                                  {!isVehicleTypeOnlyTruck && (
                                    <Col xs={12} sm={6}>
                                      <FloatingLabel
                                        controlId="trailer_number"
                                        label="Trailer number"
                                        className={styles.input}
                                      >
                                        <Form.Control
                                          type="text"
                                          placeholder="Trailer number"
                                          {...register("trailer_number", {
                                            required: !isVehicleTypeOther,
                                          })}
                                          isInvalid={!!errors.trailer_number}
                                        />
                                      </FloatingLabel>
                                    </Col>
                                  )}
                                </Row>

                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "0rem" }}
                                >
                                  <Col xs={12} sm={4}>
                                    <FloatingLabel
                                      controlId="vehicle_length"
                                      label="Length"
                                      className={styles.input}
                                    >
                                      <Form.Control
                                        type="number"
                                        step=".1"
                                        placeholder="Length"
                                        {...register("vehicle_length", {
                                          required: true,
                                        })}
                                        disabled={
                                          !isVehicleTypeOther &&
                                          !isVehicleTypeOnlyTruck
                                        }
                                        isInvalid={!!errors.vehicle_length}
                                      />
                                    </FloatingLabel>
                                  </Col>

                                  <Col xs={12} sm={4}>
                                    <FloatingLabel
                                      controlId="vehicle_width"
                                      label="Width"
                                      className={styles.input}
                                    >
                                      <Form.Control
                                        type="number"
                                        step=".1"
                                        placeholder="Width"
                                        {...register("vehicle_width", {
                                          required: true,
                                        })}
                                        disabled={!isVehicleTypeOther}
                                        isInvalid={!!errors.vehicle_width}
                                      />
                                    </FloatingLabel>
                                  </Col>

                                  {showCargoData && (
                                    <Col xs={12} sm={4}>
                                      <FloatingLabel
                                        controlId="vehicle_weight"
                                        label="Weight"
                                        className={styles.input}
                                      >
                                        <Form.Control
                                          type="number"
                                          step=".1"
                                          placeholder="Weight"
                                          {...register("vehicle_weight", {
                                            required: isWeightRequired,
                                          })}
                                          isInvalid={!!errors.vehicle_weight}
                                        />
                                      </FloatingLabel>
                                    </Col>
                                  )}
                                </Row>
                              </>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {!!(
                    isScheduleSelected &&
                    selectedVehicleType &&
                    !isVehicleTypeOnlyTruck
                  ) && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title>
                              <FaTrailer /> Trailer
                            </Card.Title>

                            <hr />

                            <Row className="gy-3">
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="trailer_type"
                                  rules={{ required: true }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      isSearchable
                                      isLoading={false}
                                      options={trailerTypeOptions}
                                      placeholder="Trailer type"
                                      className={styles.select}
                                      styles={defaultSelectStyle}
                                      isClearable
                                    />
                                  )}
                                />
                              </Col>

                              <Col />
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {!!(isScheduleSelected && showCargoData) && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title>
                              <BsBox /> Cargo
                            </Card.Title>

                            <hr />

                            <Row className="gy-3">
                              <Col xs={12} sm={6}>
                                <Controller
                                  control={control}
                                  name="cargo_type"
                                  rules={{ required: true }}
                                  render={({ field }) => (
                                    <Select
                                      {...field}
                                      isSearchable
                                      isLoading={false}
                                      options={cargoTypeOptions}
                                      filterOption={cargoTypeFilterOption}
                                      placeholder="Cargo type"
                                      className={styles.select}
                                      styles={defaultSelectStyle}
                                      isClearable
                                    />
                                  )}
                                />
                              </Col>
                              {!!(
                                selectedTrailerType &&
                                !hasTTLGroupForTrailerType
                              ) && (
                                <Col>
                                  <FloatingLabel
                                    controlId="temperature"
                                    label="Temperature"
                                    className={styles.input}
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <Form.Control
                                      type="text"
                                      placeholder="Temperature"
                                      {...register("temperature", {
                                        pattern: {
                                          value: /^[+-]?\d+(\.\d+)?$/,
                                          message:
                                            "Only + and - symbols with decimal numbers are allowed.",
                                        },
                                      })}
                                      isInvalid={!!errors.temperature}
                                    />
                                    {errors.temperature && (
                                      <Form.Text className="text-danger">
                                        {errors.temperature.message}
                                      </Form.Text>
                                    )}
                                  </FloatingLabel>
                                </Col>
                              )}
                            </Row>

                            <Row className={styles.marginTop}>
                              <Col className={styles.verticalAlign}>
                                {selectedRoute?.adr && (
                                  <Form.Check
                                    {...register("adr")}
                                    type="checkbox"
                                    id="adr"
                                    label={
                                      <div>
                                        ADR{" "}
                                        <span className={styles.infoIcon}>
                                          <BsInfoCircle />
                                        </span>
                                      </div>
                                    }
                                  />
                                )}

                                {selectedRoute?.plug_in && (
                                  <Form.Check
                                    {...register("plug_in")}
                                    type="checkbox"
                                    id="plug_in"
                                    label="Plug-in"
                                    className={styles.marginLeft}
                                  />
                                )}
                              </Col>
                              <Col />
                            </Row>

                            {adr && (
                              <Row className={styles.marginTop}>
                                <Col>
                                  <Alert variant="warning">
                                    <p>
                                      <strong>IMPORTANT!</strong>
                                    </p>
                                    <p>
                                      You are making the Order for ADR cargo.
                                    </p>
                                    <p>
                                      ADR documents must be sent to the
                                      responsible manager minimum 24 hours
                                      before departure.
                                    </p>
                                    <p>
                                      If ADR documents are not confirmed and
                                      validated with ferry manager on time the
                                      booking can be cancelled or charged
                                      additionally by the ferry company.
                                    </p>
                                    <p>
                                      Customer is fully responsible for
                                      providing ADR documents.
                                    </p>
                                    <p>
                                      *If you have any questions please contact
                                      +370 616 76859{" "}
                                    </p>
                                  </Alert>
                                  {/* <Dropzone
                                    accept={acceptedMimeTypes}
                                    onDropAccepted={handleFileUpload}
                                    onDropRejected={handleRejectedFiles}
                                    maxFiles={5}
                                    maxSize={10240000} // 10 MB
                                  >
                                    {({ getRootProps, getInputProps }) => (
                                      <div
                                        {...getRootProps()}
                                        className={styles.dropzone}
                                      >
                                        <input {...getInputProps()} />

                                        <span>
                                          Drag 'n' drop or click to select some
                                          documents.
                                        </span>

                                        {documents.length > 0 && (
                                          <ListGroup
                                            variant="flush"
                                            onClick={(event) =>
                                              event.stopPropagation()
                                            }
                                            className={styles.fileList}
                                          >
                                            {documents.map(
                                              (document, index) => (
                                                <ListGroup.Item key={index}>
                                                  <span>{document.name}</span>
                                                  <span
                                                    onClick={(event) => {
                                                      event.stopPropagation();

                                                      if (id) {
                                                        markToDeleteDocument(
                                                          document.id
                                                        );
                                                      } else {
                                                        deleteDocument(
                                                          document.id
                                                        );
                                                      }
                                                    }}
                                                    className="link-primary"
                                                  >
                                                    <BsTrash />
                                                  </span>
                                                </ListGroup.Item>
                                              )
                                            )}
                                          </ListGroup>
                                        )}
                                        {isDocumentsLoading && (
                                          <div
                                            className={styles.loadingCover}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                            }}
                                          >
                                            <Spinner
                                              size="sm"
                                              animation="border"
                                              className={styles.spinner}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Dropzone> */}
                                </Col>
                              </Row>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {!!(isScheduleSelected && selectedVehicleType) && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title className={styles.driversTitle}>
                              <div>
                                <BsPerson /> Drivers
                              </div>
                              <div>
                                <small
                                  className={`text-${
                                    errors.driver_count ? "danger" : "secondary"
                                  }`}
                                >
                                  How many drivers?
                                </small>

                                <div>
                                  <Form.Check
                                    {...register("driver_count", {
                                      required: isOtherVehicleType,
                                    })}
                                    inline
                                    label="0"
                                    value="0"
                                    type="radio"
                                    id={`drivers-0`}
                                    isInvalid={!!errors.driver_count}
                                    disabled={!isOtherVehicleType}
                                  />

                                  <Form.Check
                                    {...register("driver_count", {
                                      required: !isVehicleTypeWithoutSeats,
                                    })}
                                    inline
                                    label="1"
                                    value="1"
                                    type="radio"
                                    id={`drivers-1`}
                                    disabled={isVehicleTypeWithoutSeats}
                                    isInvalid={!!errors.driver_count}
                                  />
                                  <Form.Check
                                    {...register("driver_count", {
                                      required: !isVehicleTypeWithoutSeats,
                                    })}
                                    inline
                                    label="2"
                                    value="2"
                                    type="radio"
                                    id={`drivers-2`}
                                    disabled={isVehicleTypeWithoutSeats}
                                    isInvalid={!!errors.driver_count}
                                  />
                                </div>
                              </div>
                            </Card.Title>

                            <hr />

                            {driver_count === "0" && (
                              <Row>
                                <Col className="text-muted">
                                  No drivers selected.
                                </Col>
                              </Row>
                            )}
                            {driverFields.map((driver, index) => (
                              <React.Fragment key={index}>
                                <Row
                                  className={`${styles.driverPrefix} gy-3`}
                                  style={{ marginTop: "0rem" }}
                                >
                                  <strong className="text-secondary ">
                                    {index === 0 ? "First" : "Second"} driver
                                  </strong>
                                </Row>

                                <Row className="gy-3">
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.first_name`}
                                      label="First name"
                                      className={styles.input}
                                    >
                                      <Form.Control
                                        type="text"
                                        placeholder="First name"
                                        {...register(
                                          `drivers.${index}.first_name` as keyof BookingFormValues,
                                          { required: true, maxLength: 40 }
                                        )}
                                        isInvalid={
                                          !!errors.drivers?.[index]?.first_name
                                        }
                                      />
                                    </FloatingLabel>
                                  </Col>
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.last_name`}
                                      label="Last name"
                                      className={styles.input}
                                    >
                                      <Form.Control
                                        type="text"
                                        placeholder="Last name"
                                        {...register(
                                          `drivers.${index}.last_name` as keyof BookingFormValues,
                                          { required: true, maxLength: 40 }
                                        )}
                                        isInvalid={
                                          !!errors.drivers?.[index]?.last_name
                                        }
                                      />
                                    </FloatingLabel>
                                  </Col>
                                </Row>
                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "0rem" }}
                                >
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.phone`}
                                      label="Phone number"
                                      className={styles.input}
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                      }}
                                    >
                                      <Form.Control
                                        type="text"
                                        placeholder="Phone number"
                                        {...register(
                                          `drivers.${index}.phone` as keyof BookingFormValues,
                                          {
                                            maxLength: 30,
                                            pattern: {
                                              value: /^[0-9+\-() ]*$/,
                                              message:
                                                "Invalid phone number format.",
                                            },
                                          }
                                        )}
                                        isInvalid={
                                          !!errors.drivers?.[index]?.phone
                                        }
                                      />
                                      {errors.drivers?.[index]?.phone && (
                                        <Form.Text className="text-danger">
                                          {
                                            errors.drivers[index]?.phone
                                              ?.message
                                          }
                                        </Form.Text>
                                      )}
                                    </FloatingLabel>
                                  </Col>
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.birth_date`}
                                      label=""
                                      className={styles.input}
                                    >
                                      <Controller
                                        control={control}
                                        name={`drivers.${index}.birth_date`}
                                        render={({ field }) => (
                                          <>
                                            <Form.Control
                                              as={DatePicker}
                                              {...field}
                                              selected={
                                                field.value
                                                  ? new Date(field.value)
                                                  : null
                                              }
                                              onChange={(date) =>
                                                field.onChange(date)
                                              }
                                              dateFormat="yyyy-MM-dd"
                                              placeholderText="Birth date"
                                              showMonthDropdown
                                              showYearDropdown
                                            />
                                            <BsCalendar
                                              style={{
                                                position: "absolute",
                                                top: "50%",
                                                right: "1rem",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none",
                                                color: "#6c757d",
                                              }}
                                            />
                                          </>
                                        )}
                                      />
                                    </FloatingLabel>
                                  </Col>
                                </Row>

                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "0rem" }}
                                >
                                  <Col>
                                    <Form.Label
                                      htmlFor={`drivers.${index}.gender`}
                                      style={{ marginRight: "1rem" }}
                                    >
                                      Gender:
                                    </Form.Label>
                                    <Form.Check
                                      {...register(
                                        `drivers.${index}.gender` as keyof BookingFormValues
                                      )}
                                      inline
                                      label="Female"
                                      value="1"
                                      type="radio"
                                      id={`drivers.${index}.gender-female`}
                                      className="me-3"
                                    />
                                    <Form.Check
                                      {...register(
                                        `drivers.${index}.gender` as keyof BookingFormValues
                                      )}
                                      inline
                                      label="Male"
                                      value="2"
                                      type="radio"
                                      id={`drivers.${index}.gender-male`}
                                      className="me-3"
                                    />
                                  </Col>
                                </Row>

                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "0rem" }}
                                >
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.document_number`}
                                      label="Passport number"
                                      className={styles.input}
                                    >
                                      <Form.Control
                                        type="text"
                                        placeholder="Passport number"
                                        {...register(
                                          `drivers.${index}.document_number` as keyof BookingFormValues,
                                          { required: true, maxLength: 60 }
                                        )}
                                        isInvalid={
                                          !!errors.drivers?.[index]
                                            ?.document_number
                                        }
                                      />
                                    </FloatingLabel>
                                  </Col>
                                  <Col>
                                    <Controller
                                      control={control}
                                      name={`drivers.${index}.document_country`}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          isSearchable
                                          isClearable
                                          isLoading={isLoadingCountries}
                                          options={countryOptions}
                                          placeholder="Passport country"
                                          className={styles.select}
                                          styles={defaultSelectStyle}
                                          isDisabled={isLoadingCountries}
                                        />
                                      )}
                                    />
                                  </Col>
                                </Row>
                                <Row
                                  className="gy-3"
                                  style={{ marginTop: "0rem" }}
                                >
                                  <Col>
                                    <FloatingLabel
                                      controlId={`drivers.${index}.document_expiration_date`}
                                      label=""
                                      className={styles.input}
                                    >
                                      <Controller
                                        control={control}
                                        name={`drivers.${index}.document_expiration_date`}
                                        render={({ field }) => (
                                          <>
                                            <Form.Control
                                              as={DatePicker}
                                              {...field}
                                              selected={
                                                field.value
                                                  ? new Date(field.value)
                                                  : null
                                              }
                                              onChange={(date) =>
                                                field.onChange(date)
                                              }
                                              dateFormat="yyyy-MM-dd"
                                              placeholderText="Passport expiration"
                                              showMonthDropdown
                                              showYearDropdown
                                            />
                                            <BsCalendar
                                              style={{
                                                position: "absolute",
                                                top: "50%",
                                                right: "1rem",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none",
                                                color: "#6c757d",
                                              }}
                                            />
                                          </>
                                        )}
                                      />
                                    </FloatingLabel>
                                  </Col>
                                  <Col>
                                    <Controller
                                      control={control}
                                      name={`drivers.${index}.country`}
                                      render={({ field }) => (
                                        <Select
                                          {...field}
                                          isSearchable
                                          isClearable
                                          isLoading={isLoadingCountries}
                                          options={countryOptions}
                                          placeholder="Nationality"
                                          className={styles.select}
                                          styles={defaultSelectStyle}
                                          isDisabled={isLoadingCountries}
                                        />
                                      )}
                                    />
                                  </Col>
                                </Row>
                              </React.Fragment>
                            ))}
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}

                  {!!isScheduleSelected && (
                    <Row className={styles.marginTop}>
                      <Col>
                        <Card>
                          <Card.Body>
                            <Card.Title>
                              <BsPen /> Other
                            </Card.Title>

                            <hr />

                            <Row className={styles.marginBottom}>
                              <Col>
                                <FloatingLabel
                                  controlId="customer_reference"
                                  label="Customer reference"
                                  className={styles.input}
                                >
                                  <Form.Control
                                    type="text"
                                    placeholder="Customer reference"
                                    {...register("customer_reference")}
                                  />
                                </FloatingLabel>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  )}
                </Col>
                <Col className={styles.summaryColumn}>
                  <BookingQuickView
                    showHeader
                    departureCountry={departure_country?.label}
                    arrivalCountry={arrival_country?.label}
                    departureAt={departureAt}
                    arrivalAt={arrivalAt}
                    duration={duration}
                    company={ferryCompanyName}
                    notes={selectedRoute?.notes}
                    isTransit={selectedRoute?.is_transit}
                    sticky
                  />
                </Col>
              </Row>
            </Container>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            form="kpi-form"
            className={styles.finishBooking}
            variant="success"
            disabled={isFetching || isLoading}
          >
            <BsCheck2 /> {id ? "Update" : "Finish"} booking
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        question="Data will be lost. Continue?"
        submitText="Continue"
        isVisible={isWarningVisible}
        onHide={() => {
          setIsWarningVisible(false);
          setShow(true);
        }}
        onSubmit={() => {
          setIsWarningVisible(false);
          handleGentleClose();
        }}
      />

      <ConfirmModal
        question={`${id ? "Update" : "Create"} order?`}
        submitText={id ? "Update" : "Create"}
        isVisible={isConfirmationVisible}
        onHide={() => {
          setIsConfirmationVisible(false);
          setShow(true);
        }}
        onSubmit={() => {
          const values = getValues();

          setIsConfirmationVisible(false);

          if (id) {
            update(Number(id), values);

            setTimeout(() => {
              navigate(`/bookings/${id}`);
            }, 100);
          } else {
            create(values);

            setShow(true);
          }
        }}
      />
    </>
  );
};

export default BookingForm;
